import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  Progress,
  Typography,
  Button,
  Space,
  Tooltip,
} from "antd";
import {
  CloseOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  UploadOutlined,
  UpOutlined,
  DownOutlined,
  ArrowRightOutlined,
  ExpandAltOutlined,
} from "@ant-design/icons";
import { useUploadProgress } from "../../../../core/components/firstPartyData/providers/UploadProgressProvider";

const { Text } = Typography;

const MinimizedUploadCard = ({ onMaximize }) => {
  const {
    uploadState,
    clearUpload,
    isMinimized,
    maximizeUpload,
  } = useUploadProgress();
  const history = useHistory();

  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleViewDetails = () => {
    if (uploadState && uploadState.emailListId) {
      clearUpload();
      // Force a full navigation by setting window.location
      window.location.href = `/audience/details/${uploadState.emailListId}`;
    }
  };

  const handleRestore = () => {
    // Client-side navigation, unlike handleViewDetails above: a hard
    // navigation would tear down UploadProgressProvider's in-memory state,
    // which is the exact "stale/lost snapshot" restoring is supposed to
    // avoid. The wizard lives on the audience list page.
    if (history.location.pathname !== "/audience/main/1") {
      history.push("/audience/main/1");
    }
    maximizeUpload();
    if (onMaximize) {
      onMaximize();
    }
  };

  // Only render while explicitly minimized — this is a floating restore
  // affordance for work the user tucked away, not a second progress display
  // that shadows the wizard modal while it's open.
  if (!uploadState || !isMinimized) {
    return null;
  }

  const isPublishing = uploadState.phase === "publish";
  // "uploading" (Step 1, CSV) and "publishing" (Step 2, Nexxen) are both
  // active, in-flight work — everything else ("completed"/"failed") is
  // terminal.
  const isActive =
    uploadState.status === "uploading" || uploadState.status === "publishing";

  const getStatusIcon = () => {
    switch (uploadState.status) {
      case "uploading":
      case "publishing":
        return <LoadingOutlined style={{ color: "#1890ff" }} />;
      case "completed":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "failed":
        return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
      default:
        return <UploadOutlined />;
    }
  };

  const getStatusColor = () => {
    switch (uploadState.status) {
      case "uploading":
      case "publishing":
        return "#1890ff";
      case "completed":
        return "#52c41a";
      case "failed":
        return "#ff4d4f";
      default:
        return "#1890ff";
    }
  };

  const getStatusText = () => {
    switch (uploadState.status) {
      case "uploading":
        return "Uploading...";
      case "publishing":
        return "Publishing to Nexxen...";
      case "completed":
        return isPublishing ? "Published to Nexxen" : "Upload Complete";
      case "failed":
        return isPublishing ? "Publish Failed" : "Upload Failed";
      default:
        return "Processing";
    }
  };

  const cardStyles = {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    width: isCollapsed ? "auto" : "320px",
    zIndex: 999,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    borderRadius: "8px",
    transition: "all 0.3s ease",
  };

  const headerStyles = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: isCollapsed ? "0" : "12px",
    paddingBottom: isCollapsed ? "0" : "8px",
    borderBottom: isCollapsed ? "none" : "1px solid #f0f0f0",
  };

  return (
    <Card
      style={cardStyles}
      bodyStyle={{ padding: isCollapsed ? "12px" : "16px" }}
      bordered={false}
    >
      <div style={headerStyles}>
        <Space size="small">
          {getStatusIcon()}
          <Text strong style={{ color: getStatusColor(), fontSize: isCollapsed ? "12px" : "14px" }}>
            {isCollapsed ? `${uploadState.progress || 0}%` : getStatusText()}
          </Text>
        </Space>
        <Space size="small">
          <Tooltip title={isPublishing ? "Reopen publish status" : "Reopen upload"}>
            <Button
              type="text"
              size="small"
              icon={<ExpandAltOutlined />}
              onClick={handleRestore}
              aria-label="Reopen wizard"
            />
          </Tooltip>
          {isActive && (
            <Tooltip
              title={isCollapsed ? "Expand" : "Collapse"}
              open={undefined}
              key={isCollapsed ? "expand" : "collapse"}
            >
              <Button
                type="text"
                size="small"
                icon={isCollapsed ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setIsCollapsed(!isCollapsed)}
              />
            </Tooltip>
          )}
          {!isActive && (
            <Tooltip title="View audience details">
              <Button
                type="text"
                size="small"
                icon={<ArrowRightOutlined />}
                onClick={handleViewDetails}
              />
            </Tooltip>
          )}
          {!isActive && (
            <Tooltip title="Close">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={clearUpload}
              />
            </Tooltip>
          )}
        </Space>
      </div>

      {!isCollapsed && (
        <>
          <div style={{ marginBottom: "12px" }}>
            <Text strong style={{ fontSize: "13px", display: "block", marginBottom: "4px" }}>
              {uploadState.audienceName || "Unnamed Audience"}
            </Text>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {uploadState.dataType === "deviceIds"
                ? "Device ID"
                : uploadState.dataType === "phones"
                ? "Phone Numbers"
                : uploadState.dataType === "lookalike"
                ? "Lookalike Audience"
                : "Hashed Email"}
            </Text>
          </div>

          {isActive && (
            <div>
              <Progress
                percent={uploadState.progress || 0}
                status="active"
                strokeColor={getStatusColor()}
                size="small"
              />
              {!isPublishing && uploadState.totalRecords && (
                <Text type="secondary" style={{ fontSize: "11px", display: "block", marginTop: "4px" }}>
                  {uploadState.savedRecords || 0} / {uploadState.totalRecords} records uploaded
                </Text>
              )}
            </div>
          )}

          {uploadState.status === "completed" && (
            <div
              style={{
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: "4px",
                padding: "8px",
              }}
            >
              {isPublishing ? (
                <>
                  <Text style={{ fontSize: "12px", color: "#52c41a", display: "block", marginBottom: "4px" }}>
                    Your audience was published to Nexxen.
                  </Text>
                  <Text style={{ fontSize: "11px", color: "#389e0d" }}>
                    Nexxen typically activates it within 24 hours — check the audience details for status.
                  </Text>
                </>
              ) : (
                <>
                  <Text style={{ fontSize: "12px", color: "#52c41a", display: "block", marginBottom: "4px" }}>
                    {uploadState.savedRecords || uploadState.totalRecords} records successfully uploaded!
                  </Text>
                  <Text style={{ fontSize: "11px", color: "#389e0d" }}>
                    To activate this audience, go to the audience details and click "Map to Device IDs".
                  </Text>
                </>
              )}
            </div>
          )}

          {uploadState.status === "failed" && (
            <div
              style={{
                backgroundColor: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: "4px",
                padding: "8px",
              }}
            >
              <Text style={{ fontSize: "12px", color: "#ff4d4f" }}>
                {uploadState.errorMessage ||
                  (isPublishing
                    ? "Publish failed. Please try again."
                    : "Upload failed. Please try again.")}
              </Text>
            </div>
          )}

          {isActive && (
            <div style={{ marginTop: "12px" }}>
              <Text type="secondary" style={{ fontSize: "11px" }}>
                {isPublishing
                  ? "Publishing continues on our servers — it's safe to navigate away or close this window."
                  : "You can navigate to other pages while the upload continues in the background."}
              </Text>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default MinimizedUploadCard;
